import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../Common/Loading';

interface QnType { val: string; text: string; }
interface PkgItem { text: string; value: string; }
interface LessonItem { text: string; value: string; }

interface GridRowLesson {
  LessonCode: string; LessonName: string;
  FixedAvlEasy: number; FixedAvlMedium: number; FixedAvlDiff: number; FixedAvlALL: number;
  FixedReqEasy: number; FixedReqMed: number; FixedReqDiff: number; FixedReqALL: number;
  GenAvlEasy: number; GenAvlMedium: number; GenAvlDiff: number; GenAvlALL: number;
  GenReqEasy: number; GenReqMed: number; GenReqDiff: number; GenReqALL: number;
  Remarks: string; Questions: GridRowQuestion[];
}
interface GridRowQuestion {
  QnNo: string; QnType: string; Complexity: string; Exercise: string;
  Test: string; GenType: boolean; Truncate: string; PrcNos: string;
  NoOfClues: string; NoOfPrc: string; NoOfSolSteps: string;
}
interface GridRowTest {
  LessonCode: string; LessonName: string;
  Available: number | string; Required: number | string;
  QuestionNumbers: string; Remarks: string; UpdateTest?: string;
  isHeader?: boolean;
}

const EL_ViewLesson: React.FC = () => {
  const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;
  const TUTORIAL_APP = 'http://4.147.225.158:3000/';

  // ── State ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [ccodes, setCcodes] = useState<string[]>([]);
  const [mdCtries, setMdCtries] = useState<string[]>([]);
  const [qnTypes, setQnTypes] = useState<QnType[]>([]);
  const [testHeader, setTestHeader] = useState('');
  const [showTestHeader, setShowTestHeader] = useState(false);

  const [selSource, setSelSource] = useState('testlessons');
  const [selCountry, setSelCountry] = useState('Australia');
  const [selCC, setSelCC] = useState('--SELECT--');
  const [selMd, setSelMd] = useState('--SELECT--');

  // DIC
  const [dics, setDics] = useState<string[]>(['--Select--']);
  const [selDic, setSelDic] = useState('--Select--');
  // Package dropdown (DIC tree) — shown in ddlPackage
  const [pkgs, setPkgs] = useState<PkgItem[]>([]);
  const [selPkg, setSelPkg] = useState('');
  const [selectedPkgValues, setSelectedPkgValues] = useState<string[]>([]);
  const [hidDic, setHidDic] = useState('');
  const [hidLsns, setHidLsns] = useState('');
  const [hidTests, setHidTests] = useState('');

  // Lesson folder dropdown (YLA/MD/EC)
  const [lsnFolders, setLsnFolders] = useState<string[]>([]);
  const [selFolder, setSelFolder] = useState('');

  // Filter row
  const [maxLsn, setMaxLsn] = useState('');
  const [likeTxt, setLikeTxt] = useState('');
  const [selQnT, setSelQnT] = useState('ALL');
  const [actList, setActList] = useState<LessonItem[]>([]);
  const [selAct, setSelAct] = useState('');
  const [hitCount, setHitCount] = useState(0);

  const [txtLsn, setTxtLsn] = useState('');
  const [txtTest, setTxtTest] = useState('');

  // Grid
  const [gridMode, setGridMode] = useState<'NONE' | 'L' | 'T'>('NONE');
  const [gridLsn, setGridLsn] = useState<GridRowLesson[]>([]);
  const [gridTest, setGridTest] = useState<GridRowTest[]>([]);
  const [selQnNos, setSelQnNos] = useState<Set<string>>(new Set());

  // ── NEW: controls whether the questions sub-table is shown ───────────────
  const [showQuestions, setShowQuestions] = useState(false);

  // Right panel
  const [rBlob, setRBlob] = useState('eldata');
  const [rCountry, setRCountry] = useState('0');
  const [c2list, setC2list] = useState<string[]>([]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const isEldata = selSource === 'eldata' || selSource === 'testlessons';
  const isYLA = selSource === 'YLAEntry';
  const isMD = selSource === 'mddata' || selSource === 'ecdata';
  const showFolder = isYLA || isMD;

  const activeCountry = useCallback(() => {
    if (isYLA) return selCC;
    if (isMD) return selMd;
    return selCountry;
  }, [isYLA, isMD, selCC, selMd, selCountry]);

  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}api/view-lesson-init/`)
      .then(r => r.json()).then(d => {
        setSources(d.sources); setCountries(d.countries);
        setCcodes(d.country_codes); setMdCtries(d.md_countries);
        setQnTypes(d.qn_types);
      }).finally(() => setLoading(false));
    document.body.style.backgroundColor = "#fff";
  }, [API_BASE]);

  useEffect(() => {
    if (sources.includes("eldata")) {
      setSelSource("eldata");
    }
  }, [sources]);



  // ── Source change ─────────────────────────────────────────────────────────
  const handleSourceChange = (val: string) => {
    setSelSource(val);
    setTxtLsn(''); setTxtTest('');
    setPkgs([]); setDics(['--Select--']); setSelDic('--Select--');
    setHidDic(''); setHidLsns(''); setHidTests('');
    setLsnFolders([]); setSelFolder('');
    setSelectedPkgValues([]);
    setGridMode('NONE'); setActList([]); setHitCount(0);
    setShowQuestions(false);
    if (val === 'YLAEntry') setSelCC('--SELECT--');
    else if (val === 'mddata' || val === 'ecdata') setSelMd('--SELECT--');
    else setSelCountry(countries[0] || 'Australia');
  };

  const loadFolders = useCallback((country: string) => {
    if (!country || country === '--SELECT--') {
      setLsnFolders([]);
      return;
    }

    setLoading(true);

    fetch(
      `${API_BASE}api/view-lesson-get-lesson-folders/?source=${selSource}&country=${encodeURIComponent(country)}`
    )
      .then(r => r.json())
      .then(d => {
        const f = d.folders || [];
        setLsnFolders(f);
        setSelFolder(f[0] || '');
        if (f[0]) {
          setTxtLsn(f[0]);
        }
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [API_BASE, selSource]);

  // ── DICS ─────────────────────────────────────────────────────────────────
  const loadDics = async () => {
    const c = activeCountry();
    setLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}api/view-lesson-get-dics/?source=${selSource}&country=${encodeURIComponent(c)}`
      );
      const d = await res.json();

      setDics(d.dics || ['--Select--']);
      setSelDic(d.dics?.[0] || '--Select--');
      setPkgs([]);
    } catch (err) {
      alert("Failed to load DICs");
    } finally {
      setLoading(false);
    }
  };

  // ── DIC selection → load package tree ────────────────────────────────────
  const handleDicChange = (val: string) => {
    setSelDic(val); setHidDic(val);
    setHidLsns(''); setHidTests('');
    setPkgs([]); setSelPkg(''); setSelectedPkgValues([]);
    setGridMode('NONE');
    setShowQuestions(false);
    if (!val || val === '--Select--') return;
    const c = activeCountry();
    setLoading(true);
    fetch(`${API_BASE}api/view-lesson-get-packages/?dic_name=${encodeURIComponent(val)}&source=${selSource}&country=${encodeURIComponent(c)}`)
      .then(r => r.json()).then(d => {
        setPkgs(d.packages || []);
        setHidLsns(d.hidLessons || '');
        setHidTests(d.hidTests || '');
        setSelPkg('');
      }).finally(() => setLoading(false));
  };

  // ── Package selection → populate LessonCode / TestCode ───────────────────
  const handlePkgChange = (val: string) => {
    setSelPkg(val);
    setSelectedPkgValues(val ? [val] : []);
    if (!val) return;
    const lt = val.substring(0, 2);
    const code = val.substring(2);
    if (lt === '08') { setTxtLsn(code); setTxtTest(''); }
    else if (lt === '09') { setTxtLsn(''); setTxtTest(`${selDic}_${code}`); }
  };

  const handlePkgCheckboxChange = (value: string) => {
    if (!value) return;

    const nextValues = selectedPkgValues.includes(value)
      ? []
      : [value];
    setSelectedPkgValues(nextValues);
    setSelPkg(value);

    const lt = value.substring(0, 2);
    const code = value.substring(2);
    if (lt === '08') {
      setTxtLsn(code);
      setTxtTest('');
    } else if (lt === '09') {
      setTxtLsn('');
      setTxtTest(`${selDic}_${code}`);
    } else {
      setTxtLsn('');
      setTxtTest('');
    }
  };

  // ── Select activities ─────────────────────────────────────────────────────
  const handleSelect = () => {
    const c = activeCountry();
    setLoading(true);
    fetch(`${API_BASE}api/view-lesson-get-lessons/?source=${selSource}&country=${encodeURIComponent(c)}&like=${encodeURIComponent(likeTxt)}&max=${maxLsn}`)
      .then(r => r.json()).then(d => {
        setActList(d.lessons || []);
        setHitCount(d.count || 0);
        setSelAct('');
      }).finally(() => setLoading(false));
  };

  // ── Activity dropdown selection ───────────────────────────────────────────
  const handleActChange = (val: string) => {
    setSelAct(val);
    if (val.startsWith('L-')) { setTxtLsn(val.substring(2)); setTxtTest(''); }
    else if (val.startsWith('T-')) { setTxtTest(val.substring(2)); setTxtLsn(''); }
  };

  // ── GO ────────────────────────────────────────────────────────────────────
  const handleGo = (modeOverride?: 'L' | 'T') => {
    let mode: 'L' | 'T' | undefined = modeOverride;
    let code = '';
    const isBulk = !!modeOverride;
    setShowTestHeader(false);

    if (!mode) {
      if (txtLsn.trim()) { mode = 'L'; code = txtLsn.trim().toUpperCase(); }
      else if (txtTest.trim()) { mode = 'T'; code = txtTest.trim(); }
    } else {
      code = mode === 'L' ? hidLsns : hidTests;
    }
    if (!mode || !code) { alert('Please select a Lesson or Test code.'); return; }

    const c = activeCountry();
    setLoading(true);
    setGridMode('NONE');
    setSelQnNos(new Set());
    setShowQuestions(!isBulk);

    fetch(`${API_BASE}api/view-lesson-validate/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, code, source: selSource, country: c, dic_code: hidDic }),
    }).then(r => r.json()).then(d => {
      if (d.error) { alert(d.error); return; }
      if (mode === 'L') { setGridLsn(d.data || []); setGridMode('L'); }
      else { setTestHeader(d.header || ''); setGridTest(d.data || []); setGridMode('T'); setShowTestHeader(!isBulk); }
    }).catch(e => alert(String(e)))
      .finally(() => setLoading(false));
  };


  // ── Navigation ────────────────────────────────────────────────────────────
  const navigate = async (action: string) => {
    if (!txtLsn.trim() && !txtTest.trim()) { alert('Enter a Lesson or Test code first.'); return; }
    let eq = selCountry;
    if (isYLA) eq = selCC;
    else if (isMD) eq = selMd;

    const code = txtLsn.trim().toUpperCase() || txtTest.trim();
    const blob = selSource === 'testlessons' ? 'True' : '';
    const from = encodeURIComponent(window.location.href);
    const qnArr = Array.from(selQnNos);
    const lastQ = qnArr[qnArr.length - 1] || '';

    const lessonFolder = code + "_E";
    const dicCode = (hidDic || selDic || '').trim();




    // Direct LessonCode search does NOT require a DIC.
    // DIC is only required when the user is navigating using TestCode/package selection.
    const isDirectLessonCode = !!txtLsn.trim();

    if (!isDirectLessonCode && (!dicCode || dicCode === '--Select--')) {
      alert('Please select a DIC first.');
      return;
    }

    const p = new URLSearchParams({
      LsnTestCode: lessonFolder,
      folder: lessonFolder,
      name: code,
      EQCountry: eq,
      DBWrite: "False",
      ReturnToTutorial: "False",
      CalledFrom: from,
      ViewLesson: "Y",
    });

    // Only send diccode when one was actually selected.
    // Direct LessonCode search can work without it.
    if (dicCode && dicCode !== '--Select--') {
      p.set('diccode', dicCode);
    }
    if (blob) p.set('TestLessonsBlob', blob);

    let page = 'Tutorial';

    if (action === 'TUT') {
      if (isYLA) p.set('ylaentry', 'y');
      else if (selSource === 'mddata') p.set('ylaentry', 'mddata');
      else if (selSource === 'ecdata') p.set('ylaentry', 'ecdata');
    } else {
      page = 'Question';
      if (isYLA || isMD) {
        p.set('ylaentry', 'y');
        let yla = selSource;
        if (action === 'QNS-ANS' || action === 'SOLUTION') yla += 'Qsn-ans';
        if (action === 'RFC') yla += 'rfc-rfic';
        p.set('yla', yla);
        if (action === 'SOLUTION') p.set('solseen', 'solution');
      }

      // FIX: scope to exactly the checked questions for EVERY source
      // (eldata/testlessons/YLA/MD) and EVERY question-level action
      // (QNS, QNS-ANS, SOLUTION, RFC) — not just QNS on eldata like before.
      // If nothing is checked, no filter is sent and the whole lesson loads
      // (unchanged fallback behavior).
      if (qnArr.length) {
        p.set('QnNo', qnArr.join(','));
      }

      // Preview-mode flag — only ever set from this ViewLesson button.
      // Real students never get this param, so their flow is untouched.
      if (action === 'QNS-ANS') p.set('PreviewMode', 'ANSWERS');
      if (action === 'SOLUTION') p.set('PreviewMode', 'SOLUTION');
      if (action === 'RFC') p.set('PreviewMode', 'RFC_RFIC');
    }
    try {
      const response = await fetch(`${API_BASE}api/generate-sso-url/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login: sessionStorage.getItem("UserName"),
          schoolcode: sessionStorage.getItem("S"),
          familycode: sessionStorage.getItem("F"),

          action: action,
          page: page,

          lessoncode: code,
          questionno: lastQ,

          eqcountry: eq,

          params: p.toString(),
        }),
      });

      const data = await response.json();


      if (data.success) {
        window.location.href = data.url;
      } else {
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
      alert("Unable to start SSO.");
    }
  };

  const handleContinue = () => {
    if (txtLsn.trim()) { navigate('TUT'); return; }
    if (txtTest.trim()) {
      const pts = txtTest.split('_');
      if (pts.length >= 2) {
        const eq = isYLA ? selCC : isMD ? selMd : selCountry;
        window.location.href =
          `${TUTORIAL_APP}STUDENT/Tutorials/ConfirmTest` +
          `?DICCode=${pts[0]}&LsnTestCode=${pts.slice(1).join('_')}&EQCountry=${encodeURIComponent(eq)}&DBWrite=False`;
      }
    }
  };


  // ── CLOSE ─────────────────────────────────────────────────────────────────
  const handleClose = () => {
    // If this page was opened from another page in the same tab,
    // go back to that page.
    // if (window.history.length > 1 && document.referrer) {
    //   window.history.back();
    //   return;
    // }

    // Fallback when there is no usable browser history.
    // Go back to the SYSADMIN application instead of doing nothing.
    window.location.href = '/SysAdmin';
  };
  // ── Right panel ───────────────────────────────────────────────────────────
  const handleRightGo = () => {
    if (rCountry === '0') { alert('Select a country'); return; }
    setLoading(true);
    fetch(`${API_BASE}api/view-lesson-construct2/?country=${rCountry}&source=${rBlob}`)
      .then(r => r.json()).then(d => setC2list(d.files || []))
      .finally(() => setLoading(false));
  };
  const handleC2Click = (f: string) => {
    const code = f.replace('/', '');
    const blob = rBlob === 'testlessons' ? 'True' : '';
    window.location.href =
      `${TUTORIAL_APP}STUDENT/Tutorials/Tutorial` +
      `?LsnTestCode=${code}&EQCountry=${encodeURIComponent(rCountry)}&DBWrite=False&Type=Construct2&ReturnToTutorial=False&TestLessonsBlob=${blob}`;
  };

  const canNav = !!txtLsn.trim() || !!txtTest.trim();

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    sel: { fontSize: '14pt', color: 'blue', fontFamily: 'Arial' } as React.CSSProperties,
    inp: { fontSize: '14pt', color: 'blue', border: '1px solid #003300', fontFamily: 'Arial' } as React.CSSProperties,
    btn: { fontSize: '12pt', cursor: 'pointer', fontFamily: 'Arial' } as React.CSSProperties,
    th: { background: '#c7ddfd', border: '1px solid #80a0ff', padding: '8px', fontWeight: 'normal' as const, color: '#2859ac', textAlign: 'center' as const, whiteSpace: 'nowrap' as const } as React.CSSProperties,
    td: { borderRight: '1px solid #80a0ff', padding: '8px', textAlign: 'center' as const, fontWeight: '500', textShadow: '0.3px 0.3px 0 rgba(0, 0, 0, 0.3)', whiteSpace: 'nowrap' as const, verticalAlign: 'top' as const } as React.CSSProperties,
    sTh: { background: '#c7ddfd', border: '1px solid #80a0ff', padding: '6px', fontWeight: 'normal' as const, fontSize: '10pt', color: '#2859ac', textAlign: 'left' as const } as React.CSSProperties,
    sTd: { borderRight: '1px solid #80a0ff', padding: '8px', fontSize: '10pt', fontWeight: '500', textShadow: '0.3px 0.3px 0 rgba(0, 0, 0, 0.3)', textAlign: 'left' as const } as React.CSSProperties,
  };

  return (
    <>
      <style>{`
        .vl-root, .vl-root *, .vl-root *::before, .vl-root *::after {
          box-sizing: border-box;
        }
        .vl-root {
          width: 100%;
          display: flex;
          flex-direction: row;
          font-family: Arial, sans-serif;
          font-size: 13pt;
          box-sizing: border-box;
          max-width: 100vw;
        }
        .vl-left {
          width: 60%;
          min-width: 0;
          flex-shrink: 1;
        }
        .vl-right {
          width: 40%;
          flex-shrink: 0;
          text-align: center;
          border-left: 1px solid #ccc;
          min-width: 0;
        }
        .vl-title {
          background: teal;
          color: white;
          font-size: 16pt;
          text-align: center;
          padding: 4px;
        }
        .vl-row {
          padding: 3px 4px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 4px;
        }
        .vl-row-bot {
          border-bottom: 2px solid blue;
          margin-bottom: 4px;
          padding-bottom: 6px;
        }
        .vl-lbl {
          font-size: 13pt;
          white-space: nowrap;
        }
        .vl-grid-wrap {
          height: 78vh;
          border: 1px solid #80a0ff;
          margin-top: 4px;
          overflow-x: auto;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .vl-tbl {
          width: 100%;
          border-collapse: collapse;
          font-size: 10pt;
          margin-bottom: 20px;
        }
        .vl-stbl {
          width: 100%;
          border-collapse: collapse;
          overflow:auto
        }
        .vl-footer {
          padding: 4px;
          margin-top: 6px;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 6px;
        }
        .vl-footer-btns {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        .vl-rp-hdr {
          background: #06ad97;
          color: white;
          font-size: 24px;
          padding: 3px;
          word-break: break-word;
        }
        .vl-rp-ctrl {
          padding: 4px;
          background: #b9bdbc;
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .vl-rp-sel {
          font-size: 20px;
          color: #5141bd;
          max-width: 100%;
        }
        .vl-rp-btn {
          font-size: 20px;
          color: #ff6a00;
          font-weight: bold;
          border: none;
          background: none;
          cursor: pointer;
        }
        .vl-rp-lnk {
          color: #5668b2;
          font-size: 18px;
          padding: 2px 4px;
          cursor: pointer;
          display: block;
          text-align: left;
          word-break: break-word;
        }
        .vl-rp-lnk:hover {
          background: #56afca;
        }
        .vl-rp-list {
          max-height: 700px;
          overflow: auto;
          padding: 4px;
        }
        /* pkg select: constrain on smaller screens */
        .vl-pkg-sel {
          width: 220px;
          font-family: 'Courier New', monospace;
          max-width: 100%;
        }
        .vl-dic-sel {
          width: 150px;
          max-width: 100%;
        }
        .vl-act-sel {
          width: 150px;
          font-weight: bold;
          max-width: 100%;
        }
        .vl-lsn-inp {
          width: 150px;
          max-width: 100%;
        }

        /* ── Responsive breakpoints ── */

        /* Large desktop / laptop: slightly tighter grid so it's never cramped */
        @media (max-width: 1200px) {
          .vl-left {
            width: 58%;
          }
          .vl-right {
            width: 42%;
          }
        }

        /* Tablet: stack panels, grid shrinks */
        @media (max-width: 900px) {
          .vl-root {
            flex-direction: column;
          }
          .vl-left {
            width: 100%;
          }
          .vl-right {
            width: 100%;
            border-left: none;
            border-top: 2px solid #ccc;
          }
          .vl-grid-wrap {
            height: auto;
            max-height: 65vh;
          } 
          .vl-rp-list {
            max-height: 400px;
          }
        }

        /* Mobile: tighten inputs */
        @media (max-width: 600px) {
          .vl-root {
            font-size: 11pt;
          }
          .vl-title {
            font-size: 13pt;
          }
          .vl-lbl {
            font-size: 11pt;
          }
          .vl-row {
            padding: 4px 6px;
            gap: 6px;
          }
          .vl-grid-wrap {
            max-height: 55vh;
          }
          .vl-rp-hdr {
            font-size: 18px;
          }
          .vl-rp-sel {
            font-size: 15px;
          }
            .vl-grid-wrap thead th {
    position: sticky;
    top: 0;
    z-index: 20;
    background: #c7ddfd;
}

.vl-grid-wrap thead tr:nth-child(2) th {
    top: 40px;
}
          .vl-rp-btn {
            font-size: 16px;
          }
          .vl-rp-lnk {
            font-size: 15px;
          }
          .vl-rp-list {
            max-height: 300px;
          }
          .vl-pkg-sel {
            width: 100%;
            overflow: hidden;
          }
          .vl-dic-sel {
            width: 100%;
          }
          .vl-act-sel {
            width: 100%;
          }
          .vl-lsn-inp {
            width: 100%;
          }
          .vl-footer {
            flex-direction: column;
            align-items: stretch;
          }
          .vl-footer-btns {
            justify-content: flex-start;
          }
        }

        /* Extra small phones */
        @media (max-width: 400px) {
          .vl-title {
            font-size: 11pt;
          }
          .vl-lbl {
            font-size: 10pt;
          }
          .vl-row {
            padding: 3px 4px;
          }
          .vl-grid-wrap {
            max-height: 50vh;
          }
        }
      `}</style>

      <div className="vl-root">
        {loading && <Loading />}

        {/* ── LEFT ── */}
        <div className="vl-left">
          <div className="vl-title">VIEW LESSON / TEST</div>

          {/* Row 1 */}
          <div className="vl-row">
            <span className="vl-lbl">Blob:</span>
            <select style={s.sel} value={selSource}
              onChange={e => handleSourceChange(e.target.value)}>
              {sources.map(x => <option key={x}>{x}</option>)}
            </select>

            <span className="vl-lbl">Folder:</span>
            {isEldata && (
              <select style={s.sel} value={selCountry}
                onChange={e => setSelCountry(e.target.value)}>
                {countries.map(x => <option key={x}>{x}</option>)}
              </select>
            )}
            {isYLA && (
              <select style={s.sel} value={selCC}
                onChange={e => { setSelCC(e.target.value); setTxtLsn(''); loadFolders(e.target.value); }}>
                {ccodes.map(x => <option key={x}>{x}</option>)}
              </select>
            )}
            {isMD && (
              <select style={s.sel} value={selMd}
                onChange={e => { setSelMd(e.target.value); setTxtLsn(''); loadFolders(e.target.value); }}>
                {mdCtries.map(x => <option key={x}>{x}</option>)}
              </select>
            )}

            {showFolder && lsnFolders.length > 0 && (
              <>
                <span className="vl-lbl">Lesson Folder:</span>
                <select style={s.sel} value={selFolder}
                  onChange={e => { setSelFolder(e.target.value); setTxtLsn(e.target.value); }}>
                  {lsnFolders.map(f => <option key={f}>{f}</option>)}
                </select>
              </>
            )}

            <button style={s.btn} onClick={loadDics}>DICS</button>

            <select className="vl-dic-sel" style={s.sel} value={selDic}
              onChange={e => handleDicChange(e.target.value)}>
              {dics.map(d => <option key={d}>{d}</option>)}
            </select>

            <select
              className="vl-pkg-sel"
              style={{ ...s.sel, fontFamily: 'Courier New, monospace', fontSize: '14pt', overflow: 'hidden', textOverflow: 'ellipsis' }}
              value={selPkg}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || value.startsWith('01') || value.startsWith('03')) return;
                handlePkgChange(value);
              }}
              size={1}
            >
              {pkgs.map((p, i) => (
                <option key={i} value={p.value} style={{ color: 'blue' }}>
                  {p.text}
                </option>
              ))}
            </select>

            <span className="vl-lbl">Validate:</span>
            <button style={s.btn} disabled={!hidTests} onClick={() => handleGo('T')}>TESTS</button>
            <button style={s.btn} disabled={!hidLsns} onClick={() => handleGo('L')}>LSNs</button>
          </div>

          {/* Row 2 */}
          <div className="vl-row">
            <span className="vl-lbl">Select</span>
            <input style={{ ...s.inp, width: '40px', textAlign: 'center' }}
              value={maxLsn} onChange={e => setMaxLsn(e.target.value)} />
            <span className="vl-lbl">activities with code like:</span>
            <input style={{ ...s.inp, width: '120px', maxWidth: '100%' }}
              value={likeTxt} onChange={e => setLikeTxt(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSelect()} />
            <span className="vl-lbl">containing</span>
            <select style={s.sel} value={selQnT} onChange={e => setSelQnT(e.target.value)}>
              {qnTypes.map(t => <option key={t.val} value={t.val}>{t.text}</option>)}
            </select>
            <span className="vl-lbl">qns.</span>
            <button style={s.btn} onClick={handleSelect}>Select</button>
          </div>

          {/* Row 3 */}
          <div className={`vl-row vl-row-bot`}>
            <b>{hitCount}</b>
            <span className="vl-lbl">Activities:</span>
            <select className="vl-act-sel" style={s.sel}
              value={selAct} onChange={e => handleActChange(e.target.value)}>
              <option value="">--Select--</option>
              {actList.map(l => <option key={l.value} value={l.value}>{l.text}</option>)}
            </select>

            <span className="vl-lbl">LessonCode:</span>
            <input className="vl-lsn-inp" style={s.inp}
              value={txtLsn} onChange={e => setTxtLsn(e.target.value)} />

            <span className="vl-lbl">TestCode:</span>
            <input className="vl-lsn-inp" style={s.inp}
              value={txtTest} onChange={e => setTxtTest(e.target.value)} />

            <button style={s.btn} onClick={() => handleGo()}>GO</button>
          </div>

          {/* ── Grid ── */}
          {gridMode !== 'NONE' && (
            <div className="vl-grid-wrap">
              <table className="vl-tbl">
                <thead style={{ position: "sticky", top: "100" }}>
                  {gridMode === 'L' ? (
                    <>
                      <tr style={{ borderBottom: "1px solid #80a0ff" }}>
                        <th rowSpan={2} style={s.th}>#</th>
                        <th rowSpan={2} style={s.th}>LessonCode</th>
                        <th rowSpan={2} style={s.th}>LessonName</th>
                        {['Fixed-Available', 'Fixed-Required', 'Generated-Available', 'Generated-Required'].map(h => (
                          <th key={h} colSpan={4} style={s.th}>{h}</th>
                        ))}
                        <th rowSpan={2} style={s.th}>Remarks</th>
                      </tr>
                      <tr>
                        {[1, 2, 3, 4].map(x => (
                          <React.Fragment key={x}>
                            <th style={s.th}>Easy</th><th style={s.th}>Med</th>
                            <th style={s.th}>Diff</th><th style={s.th}>ALL</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </>
                  ) : (
                    <>
                      {showTestHeader && (
                        <tr>
                          <th
                            colSpan={7}
                            style={{
                              ...s.th,
                              fontWeight: 'bold',
                              textAlign: 'center',
                              fontSize: '18px',
                            }}
                          >
                            Test: {testHeader}
                          </th>
                        </tr>
                      )}
                      <tr style={{ borderBottom: 'none' }}>
                        {['#', 'LessonCode', 'LessonName', 'Available', 'Required', 'Question Numbers', 'Remarks']
                          .map(h => <th key={h} style={s.th}>{h}</th>)}
                      </tr>
                    </>
                  )}
                </thead>
                <tbody>
                  {gridMode === 'L'
                    ? gridLsn.map((row, i) => (
                      <React.Fragment key={i}>
                        <tr style={{
                          background: i % 2 === 0 ? '#f4faff' : 'white',
                          color: row.Remarks?.trim() ? 'red' : 'inherit',
                        }}>
                          <td style={s.td}>{i + 1}</td>
                          <td style={s.td}>{row.LessonCode}</td>
                          <td style={{ ...s.td, textAlign: 'left' }}>{row.LessonName}</td>
                          {[row.FixedAvlEasy, row.FixedAvlMedium, row.FixedAvlDiff, row.FixedAvlALL,
                          row.FixedReqEasy, row.FixedReqMed, row.FixedReqDiff, row.FixedReqALL,
                          row.GenAvlEasy, row.GenAvlMedium, row.GenAvlDiff, row.GenAvlALL,
                          row.GenReqEasy, row.GenReqMed, row.GenReqDiff, row.GenReqALL,
                          ].map((v, j) => <td key={j} style={s.td}>{v || '\u00a0'}</td>)}
                          <td style={{ ...s.td, textAlign: 'left', maxWidth: '200px', whiteSpace: 'normal' }}
                            dangerouslySetInnerHTML={{ __html: row.Remarks || '&nbsp;' }} />
                        </tr>

                        {showQuestions && row.Questions?.length > 0 && (
                          <tr>
                            <td colSpan={26} style={{ paddingTop: '40px' }}>
                              <div style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                overflow: 'auto',
                                border: '1px solid rgb(128, 160, 255)',
                              }}>
                                <table className="vl-stbl" style={{ overflow: "auto" }}>
                                  <thead style={{ position: "sticky", top: 0 }}>
                                    <tr>
                                      {['#', 'QnType', 'Complexity', 'Exercise', 'Test', 'GenType',
                                        'Truncate', 'PrcNos', 'NoOfClues', 'NoOfPrc', 'NoOfSolSteps', 'QnNo']
                                        .map(h => (
                                          <th key={h} style={{
                                            ...s.sTh,
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 10,
                                            background: '#c7ddfd',
                                          }}>{h}</th>
                                        ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.Questions.map((q, j) => (
                                      <tr key={j}
                                        onClick={() => setSelQnNos(prev => {
                                          const n = new Set(prev);
                                          n.has(q.QnNo) ? n.delete(q.QnNo) : n.add(q.QnNo);
                                          return n;
                                        })}
                                        style={{
                                          background: selQnNos.has(q.QnNo) ? '#ff9900' : j % 2 === 0 ? '#f4faff' : 'white',
                                          color: selQnNos.has(q.QnNo) ? 'white' : 'inherit',
                                          cursor: 'pointer',
                                        }}>
                                        <td style={s.sTd}>{j + 1}</td>
                                        <td style={s.sTd}>{q.QnType}</td>
                                        <td style={s.sTd}>{q.Complexity}</td>
                                        <td style={s.sTd}>{q.Exercise}</td>
                                        <td style={s.sTd}>{q.Test}</td>
                                        <td style={s.sTd}>{q.GenType ? 'Y' : ''}</td>
                                        <td style={s.sTd}>{q.Truncate}</td>
                                        <td style={s.sTd}>{q.PrcNos}</td>
                                        <td style={s.sTd}>{q.NoOfClues}</td>
                                        <td style={s.sTd}>{q.NoOfPrc}</td>
                                        <td style={s.sTd}>{q.NoOfSolSteps}</td>
                                        <td style={s.sTd}><b>{q.QnNo}</b></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                    : (showTestHeader ? gridTest.slice(1) : gridTest).map((row, i) => (
                      <tr
                        key={i}
                        style={{
                          background: i % 2 === 0 ? 'white' : '#f4faff',
                          fontSize: row.isHeader ? '12pt' : undefined,
                        }}
                      >
                        <td
                          style={{
                            ...s.td,
                            fontSize: row.isHeader ? '12pt' : undefined,
                          }}
                        >
                          {i + 1}
                        </td>
                        <td
                          style={{
                            ...s.td,
                            textAlign: 'left',
                            fontSize: row.isHeader ? '12pt' : undefined,
                          }}
                        >
                          <div dangerouslySetInnerHTML={{ __html: row.LessonCode }} />
                        </td>
                        <td style={{ ...s.td, textAlign: 'left' }}
                          dangerouslySetInnerHTML={{ __html: row.LessonName }} />
                        <td style={s.td}>{row.Available}</td>
                        <td style={s.td}>{row.Required}</td>
                        <td style={{ ...s.td, textAlign: 'left' }}
                          dangerouslySetInnerHTML={{ __html: row.QuestionNumbers }} />
                        <td style={{ ...s.td, color: 'red', textAlign: 'left', maxWidth: '250px', whiteSpace: 'normal' }}
                          dangerouslySetInnerHTML={{ __html: row.Remarks || '&nbsp;' }} />
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="vl-footer">
            <button
              type="button"
              style={s.btn}
              onClick={handleClose}
            >
              CLOSE
            </button>
            <div className="vl-footer-btns">
              {(['TUT', 'QNS', 'QNS-ANS', 'RFC\\RFIC', 'SOLUTION'] as const).map(a => (
                // {(['TUT', 'QNS', 'QNS-ANS', 'RFC\\RFIC', 'SOLUTION'] as const).map(a => (
                <button key={a} style={s.btn} disabled={!canNav}
                  // <button key={a} style={s.btn} disabled
                  onClick={() => navigate(a === 'RFC\\RFIC' ? 'RFC' : a)}>
                  {a}
                </button>
              ))}
              {/* <button style={s.btn} disabled={!canNav} onClick={handleContinue}>Continue</button> */}
              <button style={s.btn} disabled onClick={handleContinue}>Continue</button>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="vl-right">
          <div className="vl-rp-hdr">Construct2 Animation</div>
          <div className="vl-rp-ctrl">
            <span style={{ fontSize: '20px' }}>Blob:</span>
            <select className="vl-rp-sel" value={rBlob} onChange={e => setRBlob(e.target.value)}>
              <option value="eldata">eldata</option>
              <option value="testlessons">testlessons</option>
            </select>
            <span style={{ fontSize: '20px' }}>Country:</span>
            <select className="vl-rp-sel" value={rCountry} onChange={e => setRCountry(e.target.value)}>
              <option value="0">--Select Country--</option>
              {countries.map(c => <option key={c}>{c}</option>)}
            </select>
            <button className="vl-rp-btn" onClick={handleRightGo}>Go</button>
          </div>
          <div className="vl-rp-list">
            {c2list.map(f => (
              <div key={f} className="vl-rp-lnk" onClick={() => handleC2Click(f)}>
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default EL_ViewLesson;